// ============================================================
// LARCENY REPORT FORM — Public, token-authenticated
// File: src/components/LarcenyReportForm.js
// ============================================================

import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'

async function callFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) throw error
  return data
}

export default function LarcenyReportForm({ token }) {
  const [establishment, setEstablishment] = useState(null)
  const [tokenError, setTokenError]       = useState(null)
  const [tokenLoading, setTokenLoading]   = useState(true)
  const [submitted, setSubmitted]         = useState(false)
  const [submitting, setSubmitting]       = useState(false)
  const [error, setError]                 = useState(null)
  const [photos, setPhotos]               = useState([])  // { file, preview }
  const [uploadProgress, setUploadProgress] = useState('')
  const fileInputRef = useRef(null)

  const [form, setForm] = useState({
    reporter_name:    '',
    reporter_contact: '',
    incident_date:    '',
    incident_time:    '',
    description:      '',
    items_suspected:  '',
    estimated_value:  '',
  })

  useEffect(() => {
    if (!token) { setTokenError('No report link provided.'); setTokenLoading(false); return }
    callFunction('get-establishment-by-token', { token })
      .then(data => {
        if (data.error) { setTokenError(data.error) }
        else { setEstablishment(data.establishment) }
      })
      .catch(() => setTokenError('Unable to validate link. Please try again.'))
      .finally(() => setTokenLoading(false))
  }, [token])

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }))

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files)
    const remaining = 5 - photos.length
    const toAdd = files.slice(0, remaining).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }))
    setPhotos(prev => [...prev, ...toAdd])
    e.target.value = ''
  }

  const removePhoto = (index) => {
    setPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const uploadPhotos = async () => {
    if (photos.length === 0) return []

    setUploadProgress('Preparing uploads…')
    const signedData = await callFunction('create-larceny-upload-urls', {
      token,
      files: photos.map(p => ({ name: p.file.name })),
    })
    if (signedData.error) throw new Error(signedData.error)

    const paths = []
    for (let i = 0; i < photos.length; i++) {
      setUploadProgress(`Uploading photo ${i + 1} of ${photos.length}…`)
      const { signedUrl, path } = signedData.uploads[i]
      const res = await fetch(signedUrl, {
        method: 'PUT',
        body: photos[i].file,
        headers: { 'Content-Type': photos[i].file.type },
      })
      if (!res.ok) throw new Error(`Photo upload failed`)
      paths.push(path)
    }
    setUploadProgress('')
    return paths
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.description.trim()) { setError('Please describe the incident.'); return }
    setError(null)
    setSubmitting(true)
    try {
      const photo_urls = await uploadPhotos()
      const data = await callFunction('submit-larceny-report', { token, ...form, photo_urls })
      if (data.error) throw new Error(data.error)
      setSubmitted(true)
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.')
    } finally {
      setSubmitting(false)
      setUploadProgress('')
    }
  }

  // ── Styles ──────────────────────────────────────────────────
  const s = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #080f1e 0%, #0f1f3d 45%, #1e3a5f 100%)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '24px',
      paddingTop: 'max(24px, env(safe-area-inset-top))',
      overflowY: 'auto',
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
    },
    card: {
      background: '#fff',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '560px',
      maxHeight: '92vh',
      margin: 'auto 0',
      boxShadow: '0 32px 80px rgba(8,15,30,0.45)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    cardBody: {
      padding: '28px 32px 32px',
      overflowY: 'auto',
      flex: 1,
    },
    head: {
      background: 'linear-gradient(135deg, #0f1f3d 0%, #1e3a5f 100%)',
      padding: '28px 32px 24px',
    },
    logo: { height: '36px', width: 'auto', marginBottom: '16px', display: 'block' },
    headTitle: { margin: 0, fontSize: '22px', fontWeight: '600', color: '#fff', fontFamily: "'EB Garamond', Georgia, serif" },
    headSub: { margin: '6px 0 0', fontSize: '13px', color: 'rgba(168,196,232,0.75)', lineHeight: 1.5 },
    storeBadge: {
      marginTop: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '8px',
      padding: '8px 14px',
      fontSize: '13px',
      color: '#c8daf5',
      fontWeight: '600',
    },
    body: { padding: '28px 32px 32px' },
    group: { marginBottom: '16px' },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: '#1e3a5f',
      marginBottom: '6px',
    },
    optional: { fontWeight: '400', textTransform: 'none', letterSpacing: 0, color: '#7a90a8', fontSize: '11px' },
    input: {
      width: '100%',
      padding: '11px 14px',
      border: '1.5px solid rgba(61,100,148,0.18)',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#1a2a3e',
      background: '#fafcff',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
    },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    textarea: {
      width: '100%',
      padding: '11px 14px',
      border: '1.5px solid rgba(61,100,148,0.18)',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#1a2a3e',
      background: '#fafcff',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px',
      lineHeight: '1.6',
      boxSizing: 'border-box',
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
    },
    errorBox: {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '12px 14px',
      fontSize: '13px',
      color: '#991b1b',
      marginBottom: '16px',
    },
    btn: {
      width: '100%',
      padding: '14px',
      background: '#1e3a5f',
      color: '#fff',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
      marginTop: '4px',
      transition: 'background 0.15s',
    },
    successIcon: {
      width: '60px', height: '60px',
      borderRadius: '50%',
      background: '#d1fae5',
      color: '#065f46',
      fontSize: '28px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px',
    },
    photoZone: {
      border: '1.5px dashed rgba(61,100,148,0.35)',
      borderRadius: '8px',
      padding: '16px',
      background: '#f7faff',
      cursor: 'pointer',
      textAlign: 'center',
    },
    photoZoneText: {
      fontSize: '13px',
      color: '#4a6488',
      lineHeight: 1.5,
    },
    photoGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '12px',
    },
    photoThumb: {
      position: 'relative',
      borderRadius: '6px',
      overflow: 'hidden',
      width: '90px',
      height: '90px',
      flexShrink: 0,
      background: '#e8edf4',
    },
    photoRemove: {
      position: 'absolute',
      top: '4px', right: '4px',
      width: '20px', height: '20px',
      borderRadius: '50%',
      background: 'rgba(15,31,61,0.75)',
      color: '#fff',
      border: 'none',
      cursor: 'pointer',
      fontSize: '12px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      lineHeight: 1,
    },
  }

  if (tokenLoading) {
    return (
      <div style={s.page}>
        <div style={{ color: 'rgba(168,196,232,0.7)', fontSize: '15px' }}>Validating link…</div>
      </div>
    )
  }

  if (tokenError) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔗</div>
          <h2 style={{ fontSize: '20px', color: '#0f1f3d', marginBottom: '10px', fontFamily: "'EB Garamond', serif" }}>Invalid Link</h2>
          <p style={{ fontSize: '14px', color: '#7a90a8', lineHeight: 1.6 }}>{tokenError}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, padding: '48px 32px', textAlign: 'center' }}>
          <div style={s.successIcon}>✓</div>
          <h2 style={{ fontSize: '24px', color: '#0f1f3d', margin: '0 0 12px', fontFamily: "'EB Garamond', serif" }}>Report Submitted</h2>
          <p style={{ fontSize: '14px', color: '#7a90a8', lineHeight: 1.7, maxWidth: '340px', margin: '0 auto' }}>
            Your larceny report has been received and will be reviewed by an ABC enforcement agent.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.head}>
          <img src="/abcaxis-logo.png" alt="ABCAxis" style={s.logo} />
          <h1 style={s.headTitle}>Larceny Report</h1>
          <p style={s.headSub}>Submit a suspected larceny incident to your ABC enforcement agency.</p>
          <div style={s.storeBadge}>
            🏪 Store #{establishment.store_number} — {establishment.address}, {establishment.city}
          </div>
        </div>

        <div style={s.cardBody}>
          {error && <div style={s.errorBox}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={s.row}>
              <div style={s.group}>
                <label style={s.label}>Your Name <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="text" value={form.reporter_name} onChange={set('reporter_name')} placeholder="Store employee name" />
              </div>
              <div style={s.group}>
                <label style={s.label}>Contact <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="text" value={form.reporter_contact} onChange={set('reporter_contact')} placeholder="Phone or email" />
              </div>
            </div>

            <div style={s.row}>
              <div style={s.group}>
                <label style={s.label}>Incident Date <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="date" value={form.incident_date} onChange={set('incident_date')} />
              </div>
              <div style={s.group}>
                <label style={s.label}>Approximate Time <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="time" value={form.incident_time} onChange={set('incident_time')} />
              </div>
            </div>

            <div style={s.group}>
              <label style={s.label}>Comments <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea style={s.textarea} value={form.description} onChange={set('description')} placeholder="Describe what happened and any details about the suspected individual(s)…" required />
            </div>

            <div style={s.group}>
              <label style={s.label}>Items Suspected Stolen <span style={s.optional}>(optional)</span></label>
              <input style={s.input} type="text" value={form.items_suspected} onChange={set('items_suspected')} placeholder="e.g. Two bottles of Hennessy" />
            </div>

            <div style={s.group}>
              <label style={s.label}>Estimated Value <span style={s.optional}>(optional)</span></label>
              <input style={s.input} type="number" min="0" step="0.01" value={form.estimated_value} onChange={set('estimated_value')} placeholder="0.00" />
            </div>

            <div style={s.group}>
              <label style={s.label}>Photos <span style={s.optional}>(optional — up to 5)</span></label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handlePhotoSelect}
              />
              {photos.length < 5 && (
                <div style={s.photoZone} onClick={() => fileInputRef.current?.click()}>
                  <div style={{ fontSize: '22px', marginBottom: '6px' }}>📷</div>
                  <div style={s.photoZoneText}>
                    Tap to add photos<br />
                    <span style={{ fontSize: '11px', color: '#9aafcc' }}>Surveillance footage, items, or scene photos</span>
                  </div>
                </div>
              )}
              {photos.length > 0 && (
                <div style={s.photoGrid}>
                  {photos.map((p, i) => (
                    <div key={i} style={s.photoThumb}>
                      <img src={p.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        style={s.photoRemove}
                        onClick={() => removePhoto(i)}
                        aria-label="Remove photo"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{ ...s.btn, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
            >
              {uploadProgress || (submitting ? 'Submitting…' : 'Submit Report')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
